import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { mdiSortVariant } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";

import { RefLoadingSpinner } from "components/LoadingSpinner";
import { AVAILABLE_SAVED_POSTS, NOTIFICATION_FILTER_ITEM } from "constants/home";
import { routes } from "constants/routes";
import { formatDateString } from "helpers/formatPhoneNumber";
import { showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { IActivityItem } from "models/Home";
import { AccessStatus } from "models/User";
import { getActivityAPI, removeActivityByIdAPI } from "services/api/home";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { getWalletBalance } from "store/home/actions";
import { selectCardSettings } from "store/home/selector";
import NotificationItem from "../modules/NotificationItem";
import HomeAddingCard from "../modules/StatusCard/Add";
import HomeAllowedStatusCard from "../modules/StatusCard/Allowed";
import HomeInitialStatusCard from "../modules/StatusCard/Initial";
import HomePendingStatusCard from "../modules/StatusCard/Pending";
import HomeWalletStatusCard from "../modules/StatusCard/Wallet";
import styles from "./style.module.scss";
import "./_style.scss";
import { selectFactoringProfile } from "../../../../store/user/selector";

const statusCard = (status: AccessStatus, isSubscribedUser: boolean, isBroker: boolean) => {
  if (status === "approved") {
    return isBroker ? <HomeAllowedStatusCard /> : null;
  } else if (status === "pending" || isSubscribedUser) {
    return <HomePendingStatusCard />;
  } else {
    return <HomeInitialStatusCard />;
  }
};

function NotificationList({
  notificationList,
  removeActivity
}: {
  notificationList: Record<string, IActivityItem[]>;
  removeActivity: (id: string) => void;
}) {
  return (
    <>
      {Object.entries(notificationList).map(([key, value]) => (
        <li key={key} className={styles.list}>
          <h4 className={styles.title}>{formatDateString(key, "dddd MM/DD/YYYY", true)}</h4>
          <ul className={styles.listContent}>
            {value.map((item) => (
              <NotificationItem key={item._id.$oid} data={item} removeActivity={removeActivity} />
            ))}
          </ul>
        </li>
      ))}
    </>
  );
}

export default function Home() {
  const dispatch = useDispatch<AppDispatch>();
  const cardSettings = useSelector(selectCardSettings);
  const factoringProfile = useSelector(selectFactoringProfile);
  const allowSelfFinanceFr = factoringProfile?.allow_self_finance_fr ?? false;
  const { isDesktop } = useDeviceType();
  const {
    isShipper,
    isSavedLoadsRole,
    isFactoringApproved,
    isSubscribedUser,
    isBroker,
    isStripeEnabled
  } = useUserFeature();
  const [filterAlertType, setFilterAlertType] = useState("");

  const fetchActivity = async () => {
    return getActivityAPI(filterAlertType, paginationInfo.offset);
  };

  const {
    data: activityList,
    loading: isLoadingMore,
    observerRef,
    paginationInfo,
    deleteItem,
    resetPagination,
    fetch
  } = useInfiniteScroll<IActivityItem>(fetchActivity, { threshold: 0.9, filterAlertType });

  useEffect(() => {
    fetch({ offset: 0, add: false, limit: 20 });
  }, []);

  const notificationList = useMemo(() => {
    if (!activityList) return {};

    const groupedData =
      activityList.reduce(
        (grouped, item) => {
          const date = dayjs.utc(item.created_at).format("YYYY-MM-DD");

          if (!grouped[date]) {
            grouped[date] = [];
          }

          grouped[date].push({ ...item, id: item._id.$oid });

          return grouped;
        },
        {} as Record<string, IActivityItem[]>
      ) ?? {};

    return groupedData;
  }, [activityList]);

  const removeActivity = async (id: string) => {
    try {
      dispatch(handleLoading(true));
      await removeActivityByIdAPI(id);
      showSuccessMessage("Notification deleted successfully");
      deleteItem(id);
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const SavedPostsLabel = () => {
    if (!isSavedLoadsRole) {
      return AVAILABLE_SAVED_POSTS.loads;
    } else if (isShipper) {
      return AVAILABLE_SAVED_POSTS.trucks;
    } else {
      return AVAILABLE_SAVED_POSTS.posts;
    }
  };

  const fetchWalletBalance = async () => {
    dispatch(getWalletBalance());
  };

  useEffect(() => {
    if (isStripeEnabled) {
      fetchWalletBalance();
    }
  }, [isStripeEnabled]);

  const RESERVE_BALANCE = "show_reserve_balance";
  const WALLET_BALANCE = "show_wallet_balance";

  return (
    <MainLayout desktopTitle="Home" title="Home" containerClass={styles.homepageContainer}>
      {isDesktop ? (
        <>
          {(isFactoringApproved === "approved" || isSubscribedUser) && isBroker && (
            <ul className={styles.cardWrapper}>
              {cardSettings.map((wallet) => {
                if (wallet.value === WALLET_BALANCE && !allowSelfFinanceFr) {
                  return null;
                }

                // Removing the reserve balance card entirely for now, and will bring it back later
                // once we create a toggle in admin that will dictate who sees this card and who doesn't
                if (wallet.value === RESERVE_BALANCE) {
                  return null;
                }
                return wallet.show ? <HomeWalletStatusCard key={wallet.id} data={wallet} /> : null;
              })}
              <HomeAddingCard />
            </ul>
          )}
        </>
      ) : (
        statusCard(isFactoringApproved, isSubscribedUser, isBroker)
      )}

      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.decorationTopLine}></div>
          <div className={styles.title}>
            <div className="flex gap-8">
              <Link to={routes.SETTINGS_NOTIFICATIONS} className="text-info">
                Manage Notifications
              </Link>

              {!isDesktop ? (
                <Link to={SavedPostsLabel().link} className="text-info">
                  {SavedPostsLabel().label}
                </Link>
              ) : null}
            </div>

            <Menu as="div" className={styles.menuContainer}>
              <Menu.Button className="bg-transparent">
                <Icon path={mdiSortVariant} />
              </Menu.Button>

              <Transition
                as={React.Fragment}
                enter="transition ease-out duration-300"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95">
                <Menu.Items className={styles.menuBody}>
                  {NOTIFICATION_FILTER_ITEM.map((item) => (
                    <Menu.Item key={item.id}>
                      <button
                        onClick={() => {
                          setFilterAlertType(item.value);
                          resetPagination();
                        }}
                        className={clsx(
                          styles.menuItem,
                          filterAlertType === item.value && styles.activeFilterNotification
                        )}>
                        {item.label}
                      </button>
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        <ul className={clsx(styles.content, !isDesktop && "pb-20")}>
          {notificationList && Object.keys(notificationList).length > 0 ? (
            <NotificationList notificationList={notificationList} removeActivity={removeActivity} />
          ) : !isLoadingMore ? (
            <h4 className={styles.noList}>No Activities Recorded</h4>
          ) : null}
          <RefLoadingSpinner observerRef={observerRef} isLoadingMore={isLoadingMore} />
        </ul>
      </div>
    </MainLayout>
  );
}
